import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Sidebar from '../components/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  Calendar,
  Clock,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Download,
  ExternalLink,
  User,
  Mail,
  Phone,
  DollarSign,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
const DAYS_SHORT = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];

const STATUS_COLORS = {
  pending: 'bg-yellow-500',
  confirmed: 'bg-green-500',
  cancelled: 'bg-red-500',
  completed: 'bg-blue-500'
};

const STATUS_LABELS = {
  pending: 'Oczekuje',
  confirmed: 'Potwierdzona',
  cancelled: 'Anulowana',
  completed: 'Zakończona'
};

export default function BookingsPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newBooking, setNewBooking] = useState({
    service_id: '',
    date: '',
    time: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [servicesRes, availRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/booking/services`),
        axios.get(`${API}/booking/availability`),
        axios.get(`${API}/booking/bookings`)
      ]);
      setServices(servicesRes.data.services);
      setAvailability(availRes.data.availability);
      setBookings(bookingsRes.data.bookings);
    } catch (error) {
      console.error('Failed to fetch booking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveServices = async () => {
    try {
      await axios.put(`${API}/booking/services`, services);
      toast.success('Usługi zapisane');
    } catch (error) {
      toast.error('Błąd zapisu');
    }
  };

  const handleSaveAvailability = async () => {
    try {
      await axios.put(`${API}/booking/availability`, availability);
      toast.success('Dostępność zapisana');
    } catch (error) {
      toast.error('Błąd zapisu');
    }
  };

  const handleCreateBooking = async () => {
    try {
      await axios.post(`${API}/booking/bookings`, newBooking);
      toast.success('Rezerwacja utworzona');
      setShowNewBooking(false);
      setNewBooking({ service_id: '', date: '', time: '', client_name: '', client_email: '', client_phone: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd tworzenia rezerwacji');
    }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/booking/bookings/${bookingId}/status?status=${status}`);
      toast.success('Status zaktualizowany');
      fetchData();
    } catch (error) {
      toast.error('Błąd aktualizacji');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę rezerwację?')) return;
    try {
      await axios.delete(`${API}/booking/bookings/${bookingId}`);
      toast.success('Rezerwacja usunięta');
      fetchData();
    } catch (error) {
      toast.error('Błąd usuwania');
    }
  };

  const handleExportCalendar = async () => {
    try {
      const response = await axios.get(`${API}/booking/calendar.ics`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bookings.ics');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Kalendarz wyeksportowany');
    } catch (error) {
      toast.error('Błąd eksportu');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty days for alignment
    const startDay = (firstDay.getDay() + 6) % 7; // Convert to Mon=0
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getBookingsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(b => b.date === dateStr);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/bookings" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Rezerwacje</h1>
            <p className="text-muted-foreground">Zarządzaj sesjami i kalendarzem</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportCalendar}>
              <Download className="w-4 h-4 mr-2" />
              Eksport .ics
            </Button>
            <Button onClick={() => setShowNewBooking(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nowa rezerwacja
            </Button>
          </div>
        </div>

        <Tabs defaultValue="calendar" className="space-y-6">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="calendar" className="data-[state=active]:bg-secondary">
              <Calendar className="w-4 h-4 mr-2" />
              Kalendarz
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-secondary">
              <DollarSign className="w-4 h-4 mr-2" />
              Usługi
            </TabsTrigger>
            <TabsTrigger value="availability" className="data-[state=active]:bg-secondary">
              <Clock className="w-4 h-4 mr-2" />
              Dostępność
            </TabsTrigger>
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Calendar */}
              <Card className="lg:col-span-2 bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-xl">
                    {currentMonth.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAYS_SHORT.map(day => (
                      <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {getDaysInMonth(currentMonth).map((day, idx) => {
                      const dayBookings = day ? getBookingsForDate(day) : [];
                      const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();
                      const isToday = day && day.toDateString() === new Date().toDateString();
                      
                      return (
                        <div
                          key={idx}
                          onClick={() => day && setSelectedDate(day)}
                          className={`min-h-[80px] p-1 border rounded-sm cursor-pointer transition-colors ${
                            !day ? 'bg-transparent border-transparent' :
                            isSelected ? 'border-primary bg-primary/10' :
                            isToday ? 'border-primary/50 bg-secondary' :
                            'border-border hover:bg-secondary/50'
                          }`}
                        >
                          {day && (
                            <>
                              <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : ''}`}>
                                {day.getDate()}
                              </div>
                              <div className="space-y-0.5">
                                {dayBookings.slice(0, 2).map(b => (
                                  <div
                                    key={b.id}
                                    className={`text-xs px-1 py-0.5 rounded truncate text-white ${STATUS_COLORS[b.status]}`}
                                  >
                                    {b.time} {b.service_name.substring(0, 8)}
                                  </div>
                                ))}
                                {dayBookings.length > 2 && (
                                  <div className="text-xs text-muted-foreground">
                                    +{dayBookings.length - 2} więcej
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Selected Day Details */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>
                    {selectedDate ? selectedDate.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Wybierz dzień'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedDate && (
                    <div className="space-y-4">
                      {getBookingsForDate(selectedDate).length > 0 ? (
                        getBookingsForDate(selectedDate).map(booking => (
                          <div key={booking.id} className="p-3 bg-secondary rounded-sm space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{booking.time}</span>
                              <Badge className={`${STATUS_COLORS[booking.status]} text-white`}>
                                {STATUS_LABELS[booking.status]}
                              </Badge>
                            </div>
                            <p className="font-semibold">{booking.service_name}</p>
                            <div className="text-sm text-muted-foreground space-y-1">
                              <div className="flex items-center gap-2">
                                <User className="w-3 h-3" />
                                {booking.client_name}
                              </div>
                              <div className="flex items-center gap-2">
                                <Mail className="w-3 h-3" />
                                {booking.client_email}
                              </div>
                              {booking.client_phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-3 h-3" />
                                  {booking.client_phone}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 pt-2">
                              {booking.status === 'pending' && (
                                <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(booking.id, 'confirmed')}>
                                  <Check className="w-3 h-3 mr-1" />
                                  Potwierdź
                                </Button>
                              )}
                              {booking.status === 'confirmed' && (
                                <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(booking.id, 'completed')}>
                                  <Check className="w-3 h-3 mr-1" />
                                  Zakończ
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteBooking(booking.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted-foreground text-center py-8">Brak rezerwacji w tym dniu</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Twoje usługi</CardTitle>
                <Button onClick={() => setEditingService({ id: `service-${Date.now()}`, name: '', description: '', duration_minutes: 60, price: 0, color: '#3B82F6' })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Dodaj usługę
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((service, idx) => (
                    <div key={service.id} className="p-4 border border-border rounded-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: service.color }} />
                        <h3 className="font-semibold">{service.name}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{service.description}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span>{service.duration_minutes} min</span>
                        <span className="font-semibold">{service.price} zł</span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => setEditingService(service)}>
                          <Edit2 className="w-3 h-3 mr-1" />
                          Edytuj
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => {
                          setServices(services.filter((_, i) => i !== idx));
                        }}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-end">
                  <Button onClick={handleSaveServices}>Zapisz usługi</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Godziny pracy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {availability.map((slot, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-secondary/50 rounded-sm">
                      <div className="w-32 font-medium">{DAYS_OF_WEEK[slot.day_of_week]}</div>
                      <Switch
                        checked={slot.is_available}
                        onCheckedChange={(checked) => {
                          const newAvail = [...availability];
                          newAvail[idx].is_available = checked;
                          setAvailability(newAvail);
                        }}
                      />
                      {slot.is_available && (
                        <>
                          <Input
                            type="time"
                            value={slot.start_time}
                            onChange={(e) => {
                              const newAvail = [...availability];
                              newAvail[idx].start_time = e.target.value;
                              setAvailability(newAvail);
                            }}
                            className="w-32"
                          />
                          <span>-</span>
                          <Input
                            type="time"
                            value={slot.end_time}
                            onChange={(e) => {
                              const newAvail = [...availability];
                              newAvail[idx].end_time = e.target.value;
                              setAvailability(newAvail);
                            }}
                            className="w-32"
                          />
                        </>
                      )}
                      {!slot.is_available && <span className="text-muted-foreground">Niedostępny</span>}
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-end">
                  <Button onClick={handleSaveAvailability}>Zapisz dostępność</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* New Booking Modal */}
        <AnimatePresence>
          {showNewBooking && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setShowNewBooking(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-card rounded-sm max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold mb-4">Nowa rezerwacja</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Usługa</Label>
                    <Select value={newBooking.service_id} onValueChange={(v) => setNewBooking({ ...newBooking, service_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Wybierz usługę" /></SelectTrigger>
                      <SelectContent>
                        {services.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name} ({s.price} zł)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data</Label>
                      <Input type="date" value={newBooking.date} onChange={(e) => setNewBooking({ ...newBooking, date: e.target.value })} />
                    </div>
                    <div>
                      <Label>Godzina</Label>
                      <Input type="time" value={newBooking.time} onChange={(e) => setNewBooking({ ...newBooking, time: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Imię i nazwisko klienta</Label>
                    <Input value={newBooking.client_name} onChange={(e) => setNewBooking({ ...newBooking, client_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Email klienta</Label>
                    <Input type="email" value={newBooking.client_email} onChange={(e) => setNewBooking({ ...newBooking, client_email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Telefon (opcjonalnie)</Label>
                    <Input value={newBooking.client_phone} onChange={(e) => setNewBooking({ ...newBooking, client_phone: e.target.value })} />
                  </div>
                  <div>
                    <Label>Notatki (opcjonalnie)</Label>
                    <Textarea value={newBooking.notes} onChange={(e) => setNewBooking({ ...newBooking, notes: e.target.value })} rows={2} />
                  </div>
                </div>
                <div className="flex gap-2 mt-6">
                  <Button variant="outline" onClick={() => setShowNewBooking(false)} className="flex-1">Anuluj</Button>
                  <Button onClick={handleCreateBooking} className="flex-1">Utwórz</Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Service Modal */}
        <AnimatePresence>
          {editingService && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setEditingService(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-card rounded-sm max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold mb-4">{services.find(s => s.id === editingService.id) ? 'Edytuj usługę' : 'Nowa usługa'}</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Nazwa</Label>
                    <Input value={editingService.name} onChange={(e) => setEditingService({ ...editingService, name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Opis</Label>
                    <Textarea value={editingService.description} onChange={(e) => setEditingService({ ...editingService, description: e.target.value })} rows={2} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Czas trwania (min)</Label>
                      <Input type="number" value={editingService.duration_minutes} onChange={(e) => setEditingService({ ...editingService, duration_minutes: parseInt(e.target.value) })} />
                    </div>
                    <div>
                      <Label>Cena (zł)</Label>
                      <Input type="number" value={editingService.price} onChange={(e) => setEditingService({ ...editingService, price: parseFloat(e.target.value) })} />
                    </div>
                  </div>
                  <div>
                    <Label>Kolor</Label>
                    <div className="flex gap-2 mt-2">
                      {['#EC4899', '#3B82F6', '#22C55E', '#F97316', '#8B5CF6', '#EAB308'].map(color => (
                        <button
                          key={color}
                          onClick={() => setEditingService({ ...editingService, color })}
                          className={`w-8 h-8 rounded-full border-2 ${editingService.color === color ? 'border-white scale-110' : 'border-transparent'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-6">
                  <Button variant="outline" onClick={() => setEditingService(null)} className="flex-1">Anuluj</Button>
                  <Button onClick={() => {
                    const existingIdx = services.findIndex(s => s.id === editingService.id);
                    if (existingIdx >= 0) {
                      const newServices = [...services];
                      newServices[existingIdx] = editingService;
                      setServices(newServices);
                    } else {
                      setServices([...services, editingService]);
                    }
                    setEditingService(null);
                  }} className="flex-1">Zapisz</Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
